/* eslint-disable jsx-a11y/alt-text */
import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Progress, Table, Tooltip, Drawer, Popconfirm,
} from 'antd';
import {
  ExclamationCircleFilled, CaretRightFilled, CaretDownFilled,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRotate, faXmark } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { QUANTITY } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';
import closeIcon from '@Images/icons/icon-close-blue.png';
import H3Progress from '@Components/Common/H3Progress';
import MOActions from '@Actions/moActions';
import ProductCategoryLabel from '@Components/Common/ProductCategoryLabel';
import BatchesTable from '@Components/Common/BatchesTable';
import HideValue from '../../../../../Common/RestrictedAccess/HideValue';
import PRZModal from '../../../../../Common/UI/PRZModal';
import './style.scss';

const RawMaterialsLinesEntry = ({
  getMOByIdLoading, selectedManualProductionEntry, rmViewType, MONEY, user, priceMasking,
}) => {
  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;
  const [showPRZModal, setShowPRZModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const rawMaterialColumns = [
    {
      title: '',
      render: (record) => (
        <div style={{ backgroundColor: record?.fg_product ? '#ABE09835' : '#f1f9ffcc' }} className="mobile-list__item">
          <div style={{ display: 'flex' }} />
          {record?.fg_product ? (
            <div className="rm-table__fg-name">
              {`${record?.fg_product?.product_sku_info?.ref_product_code ? `${record?.fg_product?.product_sku_info?.ref_product_code} - ` : ''} ${record?.fg_product?.product_sku_info?.product_sku_name}`?.trim()}
              {' '}
              <CaretRightFilled />
            </div>
          ) : (
            <React.Fragment>
              <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`}>
                <H3Text text={`#${record?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
              </Link>
              <div className="mobile-list__item-info">
                {record?.product_sku_info?.product_sku_name}
              </div>
              <div className="mobile-list__item-completion">
                <div style={{ fontSize: '11px', width: 'calc(100% - 100px)' }}>
                  <Progress
                    percent={Number((record?.completed_quantity / record?.quantity) * 100)}
                    status="active"
                    size="small"
                    strokeColor={{ from: '#108ee9', to: '#87d068' }}
                    trailColor="#2d7df71a"
                  />
                </div>
                <H3Text text="Completed" className="mobile-list__item-completion-text" />
              </div>
            </React.Fragment>
          )}

        </div>
      ),
      responsive: ['xs'],
    },
    {
      title: '#',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      width: '40px',
      fixed: 'left',
      render: (record) => {
        const rmProductIds = selectedManualProductionEntry?.mo_lines?.filter((item) => item?.tenant_product_id === record?.tenant_product_id)?.map((line) => line?.product_sku_id) || [];
        return ({
          props: {
            style: { backgroundColor: record?.fg_product ? '#ABE09835' : 'white' },
          },
          children: <Fragment>
            {!record?.fg_product ? (
              <div style={{ whiteSpace: 'nowrap', minWidth: '40px', paddingLeft: '8px' }}>
                {`${record?.parentSqn ? `${record?.parentSqn}.` : ''}${record?.sqn}`}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className="rm-table__fg-name">
                  {record?.fg_product?.product_sku_info?.product_sku_name}
                  {' '}
                  <CaretRightFilled />
                </div>
              </div>
            )}
          </Fragment>,
        });
      },
      onCell: (item) => {
        if (item?.fg_product) {
          return { colSpan: 4 };
        }
        return {};
      },
    },
    {
      title: 'PRODUCT',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      width: '220px',
      fixed: 'left',
      render: (record) => (
        <div>
          <div className="rm-product-container">
            <div className="rm-product">
              <div onClick={() => { setShowPRZModal(true); setSelectedDocumentId(record?.product_sku_info?.product_sku_id); }}>
                <a>
                  {`#${record?.product_sku_info?.internal_sku_code}`}
                </a>
              </div>
              &nbsp;
              <span>
                {`${record?.product_sku_info?.ref_product_code ? `${record?.product_sku_info?.ref_product_code} - ` : ''}${record?.product_sku_info?.product_sku_name}`}
              </span>
            </div>
          </div>
          {record?.product_category_info?.category_path?.length > 0 && (
            <ProductCategoryLabel
              categoryPath={record?.product_category_info?.category_path}
              categoryName={record?.product_category_info?.category_path?.at(-1)}
              containerStyle={{
                width: 'fit-content',
              }}
            />
          )}
        </div>
      ),
      onCell: (item) => {
        if (item?.fg_product) {
          return { colSpan: 0 };
        }
        return {};
      },
    },
    {
      title: 'Usage',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      width: '270px',
      render: (record) => (
        <Fragment>
          <div style={{ lineHeight: '24px' }}>
            <span>
              {`${QUANTITY((record?.quantity || 0), record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}`}
            </span>
            {record?.wastage_quantity > 0 && (
              <span style={{
                fontSize: '10px',
                color: 'red',
                marginLeft: '5px',
              }}
              >
                {`(${QUANTITY(record?.wastage_quantity || 0, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()} Wastage)`}
              </span>
            )}
          </div>
        </Fragment>
      ),
      onCell: (item) => {
        if (item?.fg_product) {
          return { colSpan: 0 };
        }
        return {};
      },
    },
    {
      title: 'Incurred Cost',
      width: '120px',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => ((isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view incurred cost"} /> : MONEY(record?.rm_total_actual_incurred_cost)),
      onCell: (item) => {
        if (item?.fg_product) {
          return { colSpan: 0 };
        }
        return {};
      },
    },
  ].filter((item) => item?.hidden !== true);

  const getMoLinesWithKeys = (firstLevelExplodedViewLines, key) => {
    const getBomRawMaterials = (bomLines, parentKey) => {
      const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
      for (let i = 0; i < bomLinesCopy?.length; i++) {
        const currentItemKey = uuidv4();
        bomLinesCopy[i].key = currentItemKey;
        bomLinesCopy[i].parentKey = parentKey;
        bomLinesCopy[i].child_mo_lines = bomLinesCopy[i]?.child_mo_lines?.length ? getBomRawMaterials(bomLinesCopy[i].child_mo_lines, currentItemKey) : null;
      }
      return bomLinesCopy;
    };
    return getBomRawMaterials(firstLevelExplodedViewLines, key);
  };

  function getDataSource(data) {
    const fgRmMapping = {};
    for (let i = 0; i < data?.length; i++) {
      if (fgRmMapping.hasOwnProperty(data[i]?.fg_tenant_product_id)) {
        fgRmMapping[data[i]?.fg_tenant_product_id].push({
          ...data[i],
        });
      } else {
        fgRmMapping[data[i]?.fg_tenant_product_id] = [{
          ...data[i],
        }];
      }
    }
    const finalFgRmList = [];
    for (let i = 0; i < Object.keys(fgRmMapping)?.length; i++) {
      const fgTenantProductId = Object.keys(fgRmMapping)[i];
      if (Object.keys(fgRmMapping)?.length > 1) {
        finalFgRmList.push({
          key: uuidv4(),
          fg_product: selectedManualProductionEntry?.mo_finished_goods?.find((item) => item?.tenant_product_id == fgTenantProductId),
        });
      }
      const fgRmProductList = rmViewType === 'FIRST_LEVEL' ? getMoLinesWithKeys(fgRmMapping[fgTenantProductId]) : fgRmMapping[fgTenantProductId];
      for (let j = 0; j < fgRmProductList?.length; j++) {
        finalFgRmList.push({
          ...fgRmProductList[j],
          key: uuidv4(),
          sqn: j + 1,
          product_category_info: fgRmProductList[j]?.product_sku_info?.product_category_info,
        });
      }
    }
    return finalFgRmList;
  }

  function renderChildMoTable(childMoLines) {
    return (
      <Table
        showHeader={false}
        size="small"
        scroll={{ x: 'max-content' }}
        columns={rawMaterialColumns}
        dataSource={childMoLines?.map((item, index) => ({ ...item, sqn: index + 1 }))}
        pagination={false}
        bordered
        expandable={{
          defaultExpandedRowKeys: childMoLines?.filter((item) => item?.child_mo_lines?.length > 0)?.map((item, index) => (item?.key)),
          rowExpandable: (rec) => rec?.child_mo_lines?.length > 0 || rec?.raw_material_adjustments?.length > 0,
          expandedRowRender: (rec) => (
            <Fragment>
              <div>
                {rec?.child_mo_lines?.length ? renderChildMoTable(rec?.child_mo_lines?.map((item) => ({
                  ...item,
                  parentSqn: `${rec?.parentSqn ? `${rec?.parentSqn}.` : ''}${rec?.sqn}`,
                }))) : ''}
              </div>
            </Fragment>
          ),
        }}
        rowClassName="rm-table-row"

      />
    );
  }

  return (
    <Fragment>
      <div className="view-mo-rm-table-wrapper">
        <Table
          showHeader
          title={() => (
            <div className="rm-table-header">
              <div className="rm-table-header-title">
                <div>
                  {`${(selectedManualProductionEntry?.raw_materials?.length) || 0} Raw Materials`}
                </div>
              </div>
            </div>
          )}
          size="small"
          columns={rawMaterialColumns}
          dataSource={getDataSource(selectedManualProductionEntry?.raw_materials)}
          loading={getMOByIdLoading}
          pagination={false}
          bordered
          expandable={{
            rowExpandable: (record) => record?.product_batches?.length,
            expandedRowRender: (record) => (
              <BatchesTable
                title="Issued Batches"
                batches={record?.product_batches}
                uomInfo={record?.uom_info}
                productInfo={record?.product_sku_info}
                expiryDays={record?.product_sku_info?.expiry_days}
                showCancel
              />
            ),
          }}
          scroll={{ x: 'max-content' }}
          rowClassName="rm-table-row"
        />
      </div>
      <PRZModal
        isOpen={showPRZModal}
        onClose={() => {
          setShowPRZModal(false);
          setSelectedDocumentId(null);
        }}
        entityType="product"
        documentId={selectedDocumentId}
      />
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, MOReducers }) => ({
  MONEY: UserReducers.MONEY,
  getMOByIdLoading: MOReducers.getMOByIdLoading,
  user: UserReducers.user,
  removeRMLoading: MOReducers.removeRMLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  removeRM: (payload, callback) => dispatch(MOActions.removeRM(payload, callback)),
  getActivityLog: (entityId, entityName, page, limit, callback) => dispatch(ActivityLogActions.getActivityLog(entityId, entityName, page, limit, callback)),
  getMOById: (tenantId, moId) => dispatch(MOActions.getMOById(tenantId, moId)),
});

RawMaterialsLinesEntry.propTypes = {
  getMOByIdLoading: PropTypes.any,
  selectedManualProductionEntry: PropTypes.any,
  handleDrawerState: PropTypes.func,
  MONEY: PropTypes.func,
  rmViewType: PropTypes.string,
  user: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(RawMaterialsLinesEntry));
